import { create } from 'zustand';
import { OrdemServico, FaseOS, FaseStatus } from '../types';
import api from '../utils/api';

interface OSStore {
  ordensServico: OrdemServico[];
  ordemAtiva: OrdemServico | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchOrdensAtribuidas: () => Promise<void>;
  setOrdemAtiva: (ordem: OrdemServico | null) => void;
  iniciarFase: (osId: number, faseId: number) => Promise<boolean>;
  finalizarFase: (osId: number, faseId: number) => Promise<boolean>;
  updateFaseStatus: (faseId: number, status: 'Pendente' | 'Em Rota' | 'Concluída') => Promise<void>;
  updateLocation: (latitude: number, longitude: number) => Promise<void>;
  clearError: () => void;
}

export const useOSStore = create<OSStore>((set, get) => ({
  ordensServico: [],
  ordemAtiva: null,
  isLoading: false,
  error: null,

  fetchOrdensAtribuidas: async () => {
    set({ isLoading: true, error: null });
    try {
      // Buscar OSs onde o motorista tem fases atribuídas
      const response = await api.get('/os');
      const ordens = response.data.os || [];
      
      // Filtrar apenas OSs com fases atribuídas ao motorista logado
      const user = JSON.parse(localStorage.getItem('motorista_user') || '{}');
      const ordensAtribuidas = ordens.filter((os: OrdemServico) => 
        os.fases.some(fase => 
          fase.id_motorista_atribuido === user.id && 
          fase.status_fase !== 'Concluída'
        )
      );

      set({ 
        ordensServico: ordensAtribuidas,
        isLoading: false 
      });

      // Se não há ordem ativa, definir a primeira como ativa
      if (!get().ordemAtiva && ordensAtribuidas.length > 0) {
        set({ ordemAtiva: ordensAtribuidas[0] });
      }

    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Erro ao buscar ordens de serviço',
        isLoading: false 
      });
    }
  },

  setOrdemAtiva: (ordem) => {
    set({ ordemAtiva: ordem });
  },

  iniciarFase: async (osId: number, faseId: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.put(`/os/${osId}/iniciar-fase`, { id_fase: faseId });
      
      // Atualizar estado local
      const { ordensServico, ordemAtiva } = get();
      const ordensAtualizadas = ordensServico.map(os => {
        if (os.id === osId) {
          return {
            ...os,
            fases: os.fases.map(fase => 
              fase.id === faseId 
                ? { ...fase, status_fase: 'Em Rota' as const, timestamp_inicio_rota: new Date().toISOString() }
                : fase
            )
          };
        }
        return os;
      });

      set({ 
        ordensServico: ordensAtualizadas,
        ordemAtiva: ordensAtualizadas.find(os => os.id === osId) || null,
        isLoading: false 
      });

      return true;
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Erro ao iniciar fase',
        isLoading: false 
      });
      return false;
    }
  },

  finalizarFase: async (osId: number, faseId: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.put(`/os/${osId}/finalizar-fase`, { id_fase: faseId });
      
      // Atualizar estado local
      const { ordensServico, ordemAtiva } = get();
      const ordensAtualizadas = ordensServico.map(os => {
        if (os.id === osId) {
          return {
            ...os,
            fases: os.fases.map(fase => 
              fase.id === faseId 
                ? { ...fase, status_fase: 'Concluída' as const, timestamp_chegada: new Date().toISOString() }
                : fase
            )
          };
        }
        return os;
      });

      set({ 
        ordensServico: ordensAtualizadas,
        ordemAtiva: ordensAtualizadas.find(os => os.id === osId) || null,
        isLoading: false 
      });

      return true;
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Erro ao finalizar fase',
        isLoading: false 
      });
      return false;
    }
  },

  updateFaseStatus: async (faseId: number, status: 'Pendente' | 'Em Rota' | 'Concluída') => {
    set({ isLoading: true, error: null });
    try {
      await api.put(`/os/${faseId}/status`, { status });
      // Atualizar estado local
      const { ordensServico, ordemAtiva } = get();
      const ordensAtualizadas = ordensServico.map(os => {
        if (os.id === ordemAtiva?.id) { // Apenas a ordem ativa pode ter sua fase atualizada
          return {
            ...os,
            fases: os.fases.map(fase => 
              fase.id === faseId 
                ? { ...fase, status_fase: status }
                : fase
            )
          };
        }
        return os;
      });

      set({ 
        ordensServico: ordensAtualizadas,
        ordemAtiva: ordensAtualizadas.find(os => os.id === ordemAtiva?.id) || null,
        isLoading: false 
      });
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Erro ao atualizar status da fase',
        isLoading: false 
      });
    }
  },

  updateLocation: async (latitude: number, longitude: number) => {
    try {
      const { ordemAtiva } = get();
      if (!ordemAtiva) return;

      // Enviar localização via WebSocket (será implementado)
      // Por enquanto, apenas atualizar estado local
      set({
        ordemAtiva: {
          ...ordemAtiva,
          latitude_atual: latitude,
          longitude_atual: longitude
        }
      });
    } catch (error) {
      console.error('Erro ao atualizar localização:', error);
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
